/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Incoming-call ringing banner — pixel-matched to Figma Ping-Desktop
// 2444:7654 (card) + 4081:16281 (answer quick-reply) + 4081:16407 (decline
// menu). Card 336×76 #222429 r16; avatar 60×60 r8; stacked Answer (#3CCB4F) /
// Decline (#FF5353) split-pills (100×28 r10) each with a chevron that opens a
// dropdown. Swipe horizontally to dismiss the banner (does NOT end the call).
// Pure presentation — the host owns ringtone + the actual actions.

import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	ChevronDown,
	ChevronUp,
	Clock,
	MessageSquare,
	Phone,
	PhoneOff,
} from "lucide-react-native";
import { Animated, PanResponder, Platform, Pressable, Text, View } from "react-native";
import { Avatar } from "../../components/Avatar";
import type { CallKind } from "../types";

export interface IncomingCallBannerProps {
	readonly callerName: string;
	readonly kind: CallKind;
	readonly avatarUri?: string | undefined;
	/** Answer + join the call. */
	readonly onAnswer: () => void;
	/** Decline + end the call. */
	readonly onDecline: () => void;
	/** Approve quick action: send this canned message + dismiss banner (no end). */
	readonly onQuickReply?: ((text: string) => void) | undefined;
	/** Decline + open the conversation composer ("Reply in message"). */
	readonly onReplyInMessage?: (() => void) | undefined;
	/** Decline + remind me to call back in N minutes. */
	readonly onRemind?: ((minutes: number) => void) | undefined;
	/** Swipe-away: dismiss the banner locally WITHOUT declining the call. */
	readonly onSwipeDismiss?: (() => void) | undefined;
}

const QUICK_REPLY = "I'll be right there";
const MENU_Z = 1000;

const MenuRow: React.FC<{
	label: string;
	icon: React.ReactNode;
	onPress: () => void;
	divider?: boolean;
}> = ({ label, icon, onPress, divider }) => (
	<>
		<Pressable
			accessibilityRole="button"
			onPress={onPress}
			className="h-[28px] flex-row items-center gap-[12px] rounded-[8px] px-[12px] active:bg-white/[0.08]"
		>
			{icon}
			<Text className="text-[14px] text-[#F5F6F8]">{label}</Text>
		</Pressable>
		{divider ? <View className="my-[2px] h-px bg-[#222429]" /> : null}
	</>
);

export const IncomingCallBanner: React.FC<IncomingCallBannerProps> = ({
	callerName,
	kind,
	avatarUri,
	onAnswer,
	onDecline,
	onQuickReply,
	onReplyInMessage,
	onRemind,
	onSwipeDismiss,
}) => {
	const [menu, setMenu] = useState<"none" | "answer" | "decline">("none");
	const pan = useRef(new Animated.ValueXY()).current;

	// Horizontal swipe → dismiss (not decline). A small dx threshold avoids
	// hijacking taps on the Answer/Decline buttons.
	const panResponder = useMemo(
		() =>
			PanResponder.create({
				onMoveShouldSetPanResponder: (_e, g) =>
					Math.abs(g.dx) > 8 && Math.abs(g.dx) > Math.abs(g.dy),
				onPanResponderMove: (_e, g) => pan.setValue({ x: g.dx, y: 0 }),
				onPanResponderRelease: (_e, g) => {
					if (Math.abs(g.dx) > 90) {
						Animated.timing(pan, {
							toValue: { x: g.dx > 0 ? 600 : -600, y: 0 },
							duration: 160,
							useNativeDriver: false,
						}).start(() => onSwipeDismiss?.());
					} else {
						Animated.spring(pan, {
							toValue: { x: 0, y: 0 },
							useNativeDriver: false,
						}).start();
					}
				},
			}),
		[onSwipeDismiss, pan],
	);

	// Enter answers, Escape declines (web only — Hermes has no DOM bus).
	useEffect(() => {
		if (Platform.OS !== "web") return;
		const handler = (e: KeyboardEvent): void => {
			if (e.key === "Escape") onDecline();
			if (e.key === "Enter") onAnswer();
		};
		const doc = (globalThis as { document?: Document }).document;
		if (doc === undefined) return;
		doc.addEventListener("keydown", handler);
		return () => doc.removeEventListener("keydown", handler);
	}, [onAnswer, onDecline]);

	const eyebrow = kind === "video" ? "Incoming video call" : "Incoming call...";

	return (
		<Animated.View
			{...panResponder.panHandlers}
			style={{ transform: pan.getTranslateTransform() }}
		>
			<View
				accessibilityRole="alert"
				accessibilityLabel={`${eyebrow} from ${callerName}`}
				className="w-[336px] flex-row items-center gap-[16px] rounded-[16px] border border-[#222429] bg-[#222429] p-[8px]"
			>
				<Avatar
					size="large"
					shape="rounded"
					initials={initials(callerName)}
					{...(avatarUri !== undefined ? { imageUri: avatarUri } : {})}
				/>

				<View className="flex-1 gap-[2px]">
					<Text numberOfLines={1} className="text-[16px] font-bold text-[#F5F6F8]">
						{callerName}
					</Text>
					<Text className="text-[14px] text-white">{eyebrow}</Text>
				</View>

				{/* Stacked split-pills. The pill whose menu is open is raised so its
				    dropdown overlays the sibling pill. */}
				<View className="gap-[4px]">
					{/* Answer */}
					<View className="relative" style={{ zIndex: menu === "answer" ? MENU_Z : 1 }}>
						<View className="h-[28px] w-[100px] flex-row items-center justify-between rounded-[10px] bg-[#3CCB4F] px-[12px]">
							<Pressable
								accessibilityRole="button"
								accessibilityLabel="Answer call"
								onPress={onAnswer}
								className="flex-1 flex-row items-center active:opacity-80"
							>
								<Phone size={18} color="#FFFFFF" fill="#FFFFFF" />
							</Pressable>
							<Pressable
								accessibilityLabel="Answer options"
								onPress={() => setMenu(menu === "answer" ? "none" : "answer")}
								className="pl-[6px] active:opacity-70"
							>
								{menu === "answer" ? (
									<ChevronUp size={14} color="#E8EAED" />
								) : (
									<ChevronDown size={14} color="#E8EAED" />
								)}
							</Pressable>
						</View>
						{menu === "answer" ? (
							<View
								style={{ zIndex: MENU_Z }}
								className="absolute right-0 top-[32px] w-[180px] rounded-[12px] border border-[#2F333B] bg-[#15171C] p-[4px]"
							>
								<MenuRow
									label={QUICK_REPLY}
									icon={<MessageSquare size={16} color="#E8EAED" />}
									onPress={() => {
										setMenu("none");
										onQuickReply?.(QUICK_REPLY);
									}}
								/>
							</View>
						) : null}
					</View>

					{/* Decline */}
					<View className="relative" style={{ zIndex: menu === "decline" ? MENU_Z : 1 }}>
						<View className="h-[28px] w-[100px] flex-row items-center justify-between rounded-[10px] bg-[#FF5353] px-[12px]">
							<Pressable
								accessibilityRole="button"
								accessibilityLabel="Decline call"
								onPress={onDecline}
								className="flex-1 flex-row items-center active:opacity-80"
							>
								<PhoneOff size={18} color="#FFFFFF" />
							</Pressable>
							<Pressable
								accessibilityLabel="Decline options"
								onPress={() => setMenu(menu === "decline" ? "none" : "decline")}
								className="pl-[6px] active:opacity-70"
							>
								{menu === "decline" ? (
									<ChevronUp size={14} color="#E8EAED" />
								) : (
									<ChevronDown size={14} color="#E8EAED" />
								)}
							</Pressable>
						</View>
						{menu === "decline" ? (
							<View
								style={{ zIndex: MENU_Z }}
								className="absolute right-0 top-[32px] w-[201px] rounded-[12px] border border-[#2F333B] bg-[#15171C] p-[4px]"
							>
								<MenuRow
									label="Reply in message"
									icon={<MessageSquare size={16} color="#E8EAED" />}
									divider
									onPress={() => {
										setMenu("none");
										onReplyInMessage?.();
									}}
								/>
								<MenuRow
									label="Remind me in 5 minutes"
									icon={<Clock size={16} color="#E8EAED" />}
									onPress={() => {
										setMenu("none");
										onRemind?.(5);
									}}
								/>
								<MenuRow
									label="Remind me in 15 minutes"
									icon={<Clock size={16} color="#E8EAED" />}
									onPress={() => {
										setMenu("none");
										onRemind?.(15);
									}}
								/>
								<MenuRow
									label="Remind me in 1 hour"
									icon={<Clock size={16} color="#E8EAED" />}
									onPress={() => {
										setMenu("none");
										onRemind?.(60);
									}}
								/>
							</View>
						) : null}
					</View>
				</View>
			</View>
		</Animated.View>
	);
};

function initials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
	return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
